import { useCallback, useEffect, useState } from 'react'
import { accessToken, authFetch, authHeadersOrEmpty } from '../auth/token'
import { isOncePerTarget, type CoinProduct } from '../config/coins'
import { loadCoinsEnabled, useCoinsEnabled } from './enabled'
import { isValidShortId } from '../lib/shortId'

/**
 * 내 포인트. 서버가 진실이라 화면은 읽기만 한다.
 *
 * 스위치는 서버가 쥐고 있다 (`GET /api/coins` 의 enabled). 꺼져 있으면 아무것도 그리지 않는다.
 */

export interface CoinLedgerRow {
  id: number
  amount: number
  kind: string
  source: string
  reason: string | null
  youth?: boolean
  expires_at: string | null
  created_at: string
}

export interface CoinState {
  enabled: boolean
  signedIn: boolean
  total: number
  paid: number
  bonus: number
  history: CoinLedgerRow[]
  loading: boolean
}

const EMPTY: CoinState = { enabled: false, signedIn: false, total: 0, paid: 0, bonus: 0, history: [], loading: false }

type CoinsBody = Partial<CoinState> & { balance?: { paid: number; bonus: number } }

/** `GET /api/coins` — 401 이면 토큰을 새로 받아 한 번 더. 못 읽으면 null */
async function readCoins(): Promise<CoinsBody | null> {
  const res = await authFetch('/api/coins')
  if (!res?.ok) return null
  return (await res.json().catch(() => null)) as CoinsBody | null
}

/** 산 셋 궁합의 대상들 (지난 기록) — 두 사람 번호는 대상 끝 `p:{번호}.{번호}` 에 있다. 못 읽으면 빈 목록 */
export async function teamUnlocks(): Promise<{ target: string; at: string }[]> {
  const res = await authFetch('/api/coins/unlock?product=compatTeam&target=*').catch(() => null)
  return ((res?.ok ? await res.json().catch(() => null) : null) as { items?: { target: string; at: string }[] } | null)?.items ?? []
}

/** 장부의 다음 50줄. 잔액 계산·기록은 건드리지 않고 읽기만 한다. */
export async function loadCoinHistoryPage(offset: number): Promise<CoinLedgerRow[] | null> {
  if (!Number.isInteger(offset) || offset < 0) return null
  const headers = await authHeadersOrEmpty()
  const response = await fetch(`/api/coins?offset=${offset}`, { headers }).catch(() => null)
  if (!response?.ok) return null
  const body = (await response.json().catch(() => null)) as { history?: CoinLedgerRow[] } | null
  return Array.isArray(body?.history) ? body.history : null
}

/** 포인트 상태를 읽는다. `refresh()` 로 다시 읽는다 (충전·사용 뒤). */
export function useCoins(
  signedIn: boolean,
  version = 0,
  /**
   * **어느 계정의 잔액인가**.
   *
   * 예전 열쇠에는 계정이 없었다. A 에서 B 로 갈아타면 `signedIn` 은 계속 true 라
   * 효과가 다시 돌지 않아 **B 에게 A 의 잔액이 보였다** (제보). 계정이 바뀌면
   * 그것만으로 다시 묻고, 답이 오기 전에는 "모른다"(loading) 로 둔다.
   */
  account: string | null = null,
): CoinState & { refresh: () => void } {
  const enabled = useCoinsEnabled()
  /**
   * **어느 물음의 답인지 함께 들고 있는다**.
   *
   * `loading` 은 늘 false 였다. 그래서 잔액이 오기 전에도 화면은 "0P" 로 알고 있었고,
   * 그 사이에 유료 단추를 누르면 **"지금 0P 라 모자라요"** 가 떴다 (실측).
   * 지금 물음의 열쇠와 답의 열쇠가 다르면 **아직 모르는 것**이다.
   */
  const [state, setState] = useState<CoinState & { key: string; base: string }>({ ...EMPTY, key: '', base: '' })
  const [tick, setTick] = useState(0)
  const refresh = useCallback(() => setTick((n) => n + 1), [])
  const key = `${enabled}|${signedIn}|${account ?? ''}|${tick}|${version}`
  /** 같은 사람·같은 스위치인가 — `tick`(다시 읽기)만 다르면 새 답이 올 때까지 옛 잔액을 그대로 보여 준다 */
  const base = `${enabled}|${signedIn}|${account ?? ''}|${version}`

  useEffect(() => {
    // 스위치가 꺼져 있거나 로그인 전이면 잔액을 부르지 않는다.
    // (효과 안에서 바로 setState 하지 않는다 — 아래 반환값에서 걸러 낸다.)
    if (!enabled || !signedIn) return
    let cancelled = false
    void (async () => {
      let body = await readCoins()
      /*
       * 화면은 로그인인데 서버가 "로그인 안 함" 이라고 하면 — 토큰이 끝났거나 새로 받기가 한 번 실패했다.
       * "로그인하세요 · 0P" 를 보여 주기 전에 토큰을 새로 받아 한 번 더 묻는다.
       */
      if (body && body.signedIn !== true && (await accessToken(true))) body = await readCoins()
      if (cancelled) return
      setState({
        enabled: body?.enabled !== false,
        signedIn: body?.signedIn === true,
        total: body?.total ?? 0,
        paid: body?.balance?.paid ?? 0,
        bonus: body?.balance?.bonus ?? 0,
        history: body?.history ?? [],
        loading: false,
        key,
        base,
      })
    })()
    return () => {
      cancelled = true
    }
    // `key` 는 이 다섯 값에서 나온다 — deps 에 넣으면 같은 뜻으로 두 번 돈다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, signedIn, account, tick, version])

  // 로그인 전에는 잔액을 부르지 않으므로, 남아 있던 값을 그대로 보여 주지 않는다.
  if (!enabled || !signedIn) return { ...EMPTY, enabled, refresh }
  // 아직 이 물음의 답이 안 왔다 — 0P 라고 말하지 않고 "모른다" 고 말한다
  // 다시 읽는 중(같은 사람, tick 만 다름)이면 옛 잔액을 그대로 둔다 — 탭으로 돌아올 때마다 숫자가 깜박이지 않게
  if (state.key !== key && state.base === base) return { ...state, refresh }
  if (state.key !== key) return { ...EMPTY, enabled, signedIn: true, loading: true, refresh }
  return { ...state, refresh }
}

/**
 * **잔액을 그 자리에서 한 번 묻는다**. 유료 단추를 눌렀는데 아직 잔액을 모를 때
 * 쓴다 — "모자라요" 를 띄우기 전에 서버에 물어본다. 못 읽으면 `null` 이고,
 * 그때는 아무 말도 하지 않는다 (틀린 안내보다 조용한 편이 낫다).
 */
export async function readBalance(): Promise<{ paid: number; bonus: number; total: number } | null> {
  let body = await readCoins()
  // 토큰이 끝나 서버가 로그인 전으로 본 것이면 새로 받아 한 번 더
  if (body && body.signedIn !== true && (await accessToken(true))) body = await readCoins()
  if (!body?.balance) return null
  const paid = body.balance.paid ?? 0
  const bonus = body.balance.bonus ?? 0
  return { paid, bonus, total: paid + bonus }
}

export interface UnlockOutcome {
  ok: boolean
  /** `failed` — 서버 오류·끊김(5xx·499·네트워크). 잔액이 모자란 것이 아니다 */
  reason?: 'signin_required' | 'insufficient' | 'failed'
  spent?: number
  already?: boolean
  /** 그 상품에 드는 값. 모자랄 때 서버가 함께 준다 (`functions/api/coins/unlock.ts`) */
  cost?: number
  balance?: { paid: number; bonus: number }
}

/** 지금 사는 것이 어느 결과의 것인가. 모르면 null (서버가 아무것도 담지 않는다). */
export function buyingFrom(target: string, path: string = typeof window === 'undefined' ? '' : window.location.pathname): string | null {
  const inPath = /^\/r\/([^/?#]+)/.exec(path)?.[1]
  if (inPath && isValidShortId(inPath)) return inPath
  const head = target.split('|')[0] ?? ''
  return isValidShortId(head) ? head : null
}

/**
 * 포인트을 써서 잠금을 연다. **얼마를 깎을지는 서버가 정한다** — 여기 값은 확인창용이다.
 * 부르기 전에 화면이 확인창을 띄운다.
 */
export async function unlockWithCoins(product: CoinProduct, target: string, locale?: string): Promise<UnlockOutcome> {
  if (!(await loadCoinsEnabled())) return { ok: true, spent: 0 }
  /*
   * **어느 결과에서 사는가**. 서버가 그 사주를 산 사람의 계정에 함께 담는다 —
   * 열쇠가 맞을 때만이라 열쇠도 같이 보낸다. 결과 화면(`/r/{번호}/…`)이면 주소에서,
   * 행운 배경화면처럼 주소에 없으면 대상 값 앞자리(결과 번호)에서 찾는다.
   */
  const resultId = buyingFrom(target)
  const { resultHeaders } = await import('../lib/api')
  const headers = { 'content-type': 'application/json', ...(resultId ? await resultHeaders(resultId) : await authHeadersOrEmpty()) }
  // 언어를 함께 보낸다. **공간은 서버가 찾는다** — 화면이 보낸 공간 값은 믿지 않는다 (4단계).
  // 401(토큰이 끝났다)이면 새로 받아 한 번 다시 보낸다 — 같은 대상이라 서버가 두 번 깎지 않는다 (한 번만 사는 상품)
  const res = await authFetch('/api/coins/unlock', {
    method: 'POST',
    headers,
    body: JSON.stringify({ product, target, locale, ...(resultId ? { resultId } : {}) }),
  })
  /*
   * **"부족" 은 서버가 잔액이 모자라다고 답했을 때만**. 예전에는 끊김도 `insufficient` 로 돌려
   * 9,654P 가 있는데 "45P 필요 · -9,609P 부족" + 충전 단추가 떴다. 모자람은 402(또는 reason insufficient)뿐이다.
   */
  if (!res) return (await recoveredUnlock(product, target, locale)) ?? { ok: false, reason: 'failed' }
  const body = (await res.json().catch(() => ({}))) as UnlockOutcome
  const ok = res.ok && body.ok !== false
  if (ok) return { ...body, ok }
  if (body.reason === 'signin_required' || res.status === 401) return { ...body, ok, reason: 'signin_required' }
  if (res.status === 402 || body.reason === 'insufficient') return { ...body, ok, reason: 'insufficient' }
  return (await recoveredUnlock(product, target, locale)) ?? { ...body, ok, reason: 'failed' }
}

/**
 * **답이 끊겼어도 서버가 이미 깎았을 수 있다** (10/23 — 오류가 떴는데 새로고침하니 45P 가 빠지고 리포트가 열려 있었다).
 * "포인트를 쓰지 못했어요" 를 띄우기 전에 그 대상이 열렸는지 한 번 묻는다. 열렸으면 산 것으로 이어 간다
 * (한 번만 사는 상품만 — 열림 기록이 남는 상품이다. 다시 눌러도 서버가 0P 로 넘긴다).
 */
async function recoveredUnlock(product: CoinProduct, target: string, locale?: string): Promise<UnlockOutcome | null> {
  if (!isOncePerTarget(product)) return null
  const q = new URLSearchParams({ product, target, ...(locale ? { locale } : {}) })
  const res = await authFetch(`/api/coins/unlock?${q}`)
  if (!res?.ok) return null
  const body = (await res.json().catch(() => null)) as { unlocked?: boolean; balance?: { paid: number; bonus: number } } | null
  if (body?.unlocked !== true) return null
  return { ok: true, spent: 0, already: true, ...(body.balance ? { balance: body.balance } : {}) }
}

